import { el, esc, setText, toggle } from './dom';
import { formatClock, formatDistance, heatName, heatState, touchWording } from './format';
import { touchUi } from '../input/touch';
import { CONTROL_ICONS, HEAT_ICONS } from './icons';
import { RollingCounter } from './rolling';
import type { HudOnline, ObjectiveInfo, ToastKind } from './uiTypes';
import { formatMoney } from '../economy/wallet';

/** A short, still tap (or a click) on the minimap opens the full map; a drag or a long press does not. */
const MAP_TAP_MS = 450;
const MAP_TAP_PX = 12;

/**
 * The objective clipping on a phone (or any short / narrow window) is a slim strip next to the minimap:
 * title, the line cut to one row, the distance. A new objective opens the whole clipping (hint and all)
 * for OBJ_OPEN_MS, a tap opens it for OBJ_TAP_OPEN_MS; both fold back to the strip on their own.
 */
const OBJ_OPEN_MS = 4_800;
const OBJ_TAP_OPEN_MS = 9_000;
const OBJ_COMPACT_QUERY = '(max-height: 520px), (max-width: 640px)';

/** Everything the HUD needs for one frame, gathered by the UI system from services. */
export interface HudFrame {
  dt: number;
  money: number;
  heat: number;
  seen: boolean;
  /** On-foot health 0..1, null = hide (full health). */
  health01: number | null;
  vehicle: null | {
    name: string;
    mph: number;
    health01: number;
    gear: number;
    burning: boolean;
    radio: string;
    /** The radio text is a news-desk bulletin (it wraps and stays up longer). */
    radioNews?: boolean;
  };
  street: string;
  district: string;
  prompt: null | { text: string; sx: number; sy: number; key: string };
}

const MAX_TOASTS = 4;
const MAX_TOASTS_TOUCH = 2;
/** Speedometer dial: 0..SPEEDO_MAX mph over a 270° sweep, needle angle clockwise from 12 o'clock. */
const SPEEDO_MAX = 140;
const SPEEDO_FROM = -135;
const SPEEDO_SWEEP = 270;
const RADIO_TICKER_S = 4.5;
/** A news-desk bulletin stays up long enough to read (two short sentences). */
const RADIO_BULLETIN_S = 9;

export function needleAngle(mph: number): number {
  return SPEEDO_FROM + (Math.max(0, Math.min(SPEEDO_MAX + 4, mph)) / SPEEDO_MAX) * SPEEDO_SWEEP;
}

/** Where a station sits on the 88-108 tuning scale, in % (from the "98.4" in its name; centre if none). */
export function radioDialPos(name: string): number {
  const m = /\b(\d{2,3}(?:\.\d)?)\b/.exec(name);
  const f = m ? Number(m[1]) : NaN;
  if (!(f >= 88 && f <= 108)) return 50;
  return ((f - 88) / 20) * 100;
}

/** The dial face (SVG, 200 x 200): ticks every 5 mph, numerals every 20 (not 0 / 140: the odometer sits between them), a red band from 120, the body arc. */
function speedoFace(): string {
  const c = 100;
  const pt = (deg: number, r: number): string => {
    const a = ((deg - 90) * Math.PI) / 180;
    return `${(c + Math.cos(a) * r).toFixed(1)} ${(c + Math.sin(a) * r).toFixed(1)}`;
  };
  const arc = (from: number, to: number, r: number): string => `M${pt(from, r)}A${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${pt(to, r)}`;
  let ticks = '';
  let nums = '';
  for (let m = 0; m <= SPEEDO_MAX; m += 5) {
    const a = needleAngle(m);
    const major = m % 20 === 0;
    const len = major ? 13 : m % 10 === 0 ? 8 : 5;
    ticks += `<path class="${major ? 'maj' : 'min'}" d="M${pt(a, 86)}L${pt(a, 86 - len)}"/>`;
    if (major && m > 0 && m < SPEEDO_MAX) {
      const [x, y] = pt(a, 60).split(' ');
      nums += `<text x="${x}" y="${y}">${m}</text>`;
    }
  }
  return (
    `<svg class="face" viewBox="0 0 200 200" aria-hidden="true">` +
    `<circle class="bg" cx="100" cy="100" r="96"/>` +
    `<path class="red" d="${arc(needleAngle(120), needleAngle(SPEEDO_MAX), 83)}"/>` +
    `<g class="ticks">${ticks}</g><g class="nums">${nums}</g>` +
    `<text class="unit" x="100" y="64">MPH</text>` +
    `<path class="hp-bg" d="${arc(152, 208, 88)}"/>` +
    `<path class="hp-fill" pathLength="100" d="${arc(152, 208, 88)}"/>` +
    `<g class="needle"><path d="M98.2 114 L99.4 22 L100.6 22 L101.8 114 Z"/><circle class="hub" cx="100" cy="100" r="8"/></g>` +
    `</svg>`
  );
}

export interface HudElements {
  root: HTMLElement;
  /** Container the minimap canvas lives in. */
  mapCanvas: HTMLCanvasElement;
}

/**
 * Builds the HUD DOM once. All per-frame updates go through `update` and only touch the DOM when a
 * value actually changed.
 */
export class Hud {
  readonly root: HTMLElement;
  readonly mapCanvas: HTMLCanvasElement;
  private readonly money = new RollingCounter(0);
  private readonly moneyBox: HTMLElement;
  private readonly moneyAmt: HTMLElement;
  private moneyShown = -1;
  private moneyClass = 0;
  private moneyClassTimer = 0;

  private readonly heatBox: HTMLElement;
  private readonly heatBadge: HTMLElement;
  private readonly heatName: HTMLElement;
  private readonly heatState: HTMLElement;
  private readonly pips: HTMLElement[] = [];
  private heatLevel = -1;
  private heatSeen: boolean | null = null;

  private readonly healthBox: HTMLElement;
  private readonly healthFill: HTMLElement;

  private readonly objBox: HTMLElement;
  private readonly objTitle: HTMLElement;
  private readonly objTag: HTMLElement;
  private readonly objText: HTMLElement;
  private readonly objHint: HTMLElement;
  private readonly objWarn: HTMLElement;
  private readonly objWarnText: HTMLElement;
  private readonly objWarnClock: HTMLElement;
  private readonly objMeta: HTMLElement;
  private readonly objDist: HTMLElement;
  private readonly objTimer: HTMLElement;
  private readonly objBar: HTMLElement;
  private readonly objBarFill: HTMLElement;
  private readonly objSteps: HTMLElement;
  private objKey = '';
  /** The content part of objKey (no distance/timer flags): a change re-opens the strip. */
  private objContentKey = '';
  private objFold: number | null = null;
  private readonly objCompactMq: MediaQueryList | null =
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(OBJ_COMPACT_QUERY) : null;

  private readonly vehBox: HTMLElement;
  private readonly vehName: HTMLElement;
  private readonly vehSpeed: HTMLElement;
  private readonly vehGear: HTMLElement;
  private readonly vehNeedle: SVGGElement;
  private readonly vehHpArc: SVGPathElement;
  private readonly vehWarn: HTMLElement;
  private readonly vehAlert: HTMLElement;
  private readonly radioBox: HTMLElement;
  private readonly radioPointer: HTMLElement;
  private readonly vehRadio: HTMLElement;
  private vehMph = -1;
  private vehHp = -1;
  private radioText: string | null = null;
  private radioTimer = 0;

  private readonly streetBox: HTMLElement;
  private readonly streetName: HTMLElement;
  private readonly streetDistrict: HTMLElement;
  private readonly mapBox: HTMLElement;
  private mapTap: { id: number; x: number; y: number; t: number } | null = null;
  /** Set by the UI system: a tap / click on the minimap. Returns false when the tap should be ignored. */
  onMapTap: (() => boolean) | null = null;

  // Online additions (src/online-ui pushes them through `setOnline`): all in this layer, under every overlay.
  private readonly online: HudOnline = { net: null, bank: null, cargo: null, law: null, hotkeys: null };
  private readonly bankLine: HTMLElement;
  private readonly bankAmt: HTMLElement;
  private readonly netChip: HTMLElement;
  private readonly netChipText: HTMLElement;
  private readonly lawBox: HTMLElement;
  private readonly cargoBox: HTMLElement;
  private readonly cargoText: HTMLElement;
  private readonly cargoFill: HTMLElement;
  private readonly hotkeyRow: HTMLElement;
  private lawHtml = '';

  private readonly promptEl: HTMLElement;
  private readonly promptKey: HTMLElement;
  private readonly promptText: HTMLElement;

  private readonly toasts: HTMLElement;
  private readonly districtCard: HTMLElement;
  private readonly districtName: HTMLElement;
  private readonly districtTags: HTMLElement;
  private readonly sayBox: HTMLElement;
  private readonly sayWho: HTMLElement;
  private readonly sayLine: HTMLElement;
  private sayTimer = 0;
  private readonly bars: HTMLElement;
  private readonly flash: HTMLElement;
  private readonly hints: HTMLElement;
  private hintsTimer = 14;
  private readonly timers = new Set<number>();

  constructor(parent: HTMLElement) {
    this.root = el('div', 'bh-hud', undefined, parent);
    const r = this.root;

    // Cash.
    this.moneyBox = el('div', 'hud-money bh-soot', undefined, r);
    el('span', 'lbl', 'CASH', this.moneyBox);
    el('span', 'cur', '£', this.moneyBox);
    this.moneyAmt = el('span', 'amt', '0', this.moneyBox);
    // Online: the bank balance as a second line of the same plate (it was a separate tag in another style).
    this.bankLine = el('span', 'bank', undefined, this.moneyBox);
    el('span', 'blbl', 'BANK', this.bankLine);
    this.bankAmt = el('b', '', '£0', this.bankLine);
    this.bankLine.hidden = true;

    // Heat.
    this.heatBox = el('div', 'hud-heat bh-soot lvl0', undefined, r);
    this.heatBadge = el('div', 'badge', undefined, this.heatBox);
    const info = el('div', 'info', undefined, this.heatBox);
    this.heatName = el('div', 'name', 'Unnoticed', info);
    this.heatState = el('div', 'state', 'Nobody is looking', info);
    const pips = el('div', 'pips', undefined, info);
    for (let i = 0; i < 4; i++) this.pips.push(el('span', 'pip', undefined, pips));

    this.healthBox = el('div', 'hud-health idle', undefined, r);
    this.healthFill = el('i', '', undefined, this.healthBox);

    // Objective clipping. It shares a column with the toasts: on a phone the toasts stack under it
    // (touch.css .hud-notes), on a desktop both keep their own corner of the screen.
    const notes = el('div', 'hud-notes', undefined, r);
    this.objBox = el('div', 'hud-objective bh-glass is-empty', undefined, notes);
    const head = el('div', 'head', undefined, this.objBox);
    this.objTitle = el('div', 'title', '', head);
    this.objTag = el('span', 'bh-tag', 'THE JOB', head);
    // Red warning strip (walking off from the job's car): text + optional countdown.
    this.objWarn = el('div', 'warn', undefined, this.objBox);
    this.objWarnText = el('span', 'txt', '', this.objWarn);
    this.objWarnClock = el('span', 'clock', '', this.objWarn);
    this.objWarn.hidden = true;
    this.objText = el('div', 'text', '', this.objBox);
    this.objSteps = el('ul', 'steps', undefined, this.objBox);
    this.objMeta = el('div', 'meta', undefined, this.objBox);
    this.objDist = el('span', 'chip dist', '', this.objMeta);
    this.objTimer = el('span', 'chip timer', '', this.objMeta);
    this.objBar = el('div', 'bar', undefined, this.objMeta);
    this.objBarFill = el('i', '', undefined, this.objBar);
    this.objHint = el('div', 'hint', '', this.objBox);
    this.objBox.addEventListener('pointerdown', this.onObjDown);

    // Minimap. A tap (touch) or a click (mouse) on it opens the full map, like MAP / M.
    const map = el('div', 'hud-map', undefined, r);
    this.mapBox = map;
    map.setAttribute('role', 'button');
    map.setAttribute('aria-label', 'Map');
    map.title = 'The whole borough (M)';
    el('div', 'ring', undefined, map);
    this.mapCanvas = el('canvas', '', undefined, map);
    this.mapCanvas.width = 480;
    this.mapCanvas.height = 480;
    el('div', 'vignette', undefined, map);
    this.streetBox = el('div', 'street bh-soot', undefined, map);
    this.streetName = el('b', '', '', this.streetBox);
    this.streetDistrict = el('span', '', '', this.streetBox);
    map.addEventListener('pointerdown', this.onMapDown);
    map.addEventListener('pointerup', this.onMapUp);
    map.addEventListener('pointercancel', () => (this.mapTap = null));
    // No click after the touch that opened the map: the browser would aim it at the full map's backdrop.
    map.addEventListener('touchend', (e) => e.cancelable && e.preventDefault(), { passive: false });
    map.addEventListener('click', (e) => e.preventDefault());

    // Online strips: on a desktop the chip under the minimap, the law strip above it, the cargo strip over
    // the speedometer. On a phone (touch.css) the chip sits under the minimap's street plate, the law strip
    // takes the tip slip's band between the thumbs (the tip waits for it), and the cargo strip sits under
    // the heat plate top right: a column under the minimap ran into the toasts, the tip and the stick.
    const strips = el('div', 'hud-strips', undefined, r);
    this.netChip = el('div', 'hud-net bh-soot', undefined, strips);
    el('i', 'dot', undefined, this.netChip);
    this.netChipText = el('span', 'txt', '', this.netChip);
    this.netChip.hidden = true;
    this.lawBox = el('div', 'hud-law bh-glass', undefined, strips);
    this.lawBox.hidden = true;
    this.cargoBox = el('div', 'hud-cargo bh-soot', undefined, strips);
    this.cargoText = el('span', '', '', this.cargoBox);
    const cargoBar = el('span', 'bar', undefined, this.cargoBox);
    this.cargoFill = el('i', '', undefined, cargoBar);
    this.cargoBox.hidden = true;

    // Vehicle: a round period speedometer (chrome bezel, MPH dial, needle), an odometer-drum read-out of
    // speed + gear, a body-damage arc and warning lamps, and the car's name on a badge plate.
    this.vehBox = el('div', 'hud-vehicle is-hidden', undefined, r);
    const dial = el('div', 'dial', undefined, this.vehBox);
    dial.innerHTML = speedoFace();
    this.vehNeedle = dial.querySelector('.needle') as SVGGElement;
    this.vehHpArc = dial.querySelector('.hp-fill') as SVGPathElement;
    const odo = el('div', 'odo', undefined, dial);
    this.vehSpeed = el('b', 'mph', '000', odo);
    this.vehGear = el('b', 'gear', 'N', odo);
    const lamps = el('div', 'lamps', undefined, dial);
    this.vehWarn = el('span', 'lamp warn', undefined, lamps);
    this.vehWarn.innerHTML = CONTROL_ICONS.warn;
    this.vehAlert = el('span', 'lamp fire', undefined, lamps);
    this.vehAlert.innerHTML = CONTROL_ICONS.fire;
    this.vehAlert.title = 'ON FIRE!';
    this.vehName = el('div', 'plate', '', this.vehBox);

    // Radio: a little tuning-dial ticker that slides in when the station changes, then fades.
    this.radioBox = el('div', 'hud-radio', undefined, r);
    const scale = el('div', 'scale', undefined, this.radioBox);
    scale.innerHTML = '<span>88</span><span>92</span><span>96</span><span>100</span><span>104</span><span>108</span>';
    this.radioPointer = el('i', 'pointer', undefined, scale);
    this.vehRadio = el('div', 'radio', '', this.radioBox);

    // Prompt, toasts, district, subtitles, bars, flash, hints.
    this.promptEl = el('div', 'hud-prompt bh-glass', undefined, r);
    this.promptEl.hidden = true;
    this.promptKey = el('kbd', '', 'E', this.promptEl);
    this.promptText = el('span', '', '', this.promptEl);

    this.toasts = el('div', 'hud-toasts', undefined, notes);
    this.districtCard = el('div', 'hud-district', undefined, r);
    el('div', 'kick', 'NOW ENTERING', this.districtCard);
    this.districtName = el('div', 'name', '', this.districtCard);
    this.districtTags = el('div', 'tags', '', this.districtCard);

    this.sayBox = el('div', 'hud-say bh-glass is-hidden', undefined, r);
    this.sayWho = el('div', 'who', '', this.sayBox);
    this.sayLine = el('div', 'line', '', this.sayBox);

    this.bars = el('div', 'bh-bars', undefined, r);
    this.flash = el('div', 'bh-flash', undefined, r);
    this.hints = el('div', 'hud-hints', undefined, r);
    // Online the letter hotkeys sit in a row above the keyboard hints and fade with them (they were a
    // permanent second row of ten chips); the book's STREET page keeps the list.
    this.hotkeyRow = el('div', 'row online', undefined, this.hints);
    this.hotkeyRow.hidden = true;
    const keys = el('div', 'row keys', undefined, this.hints);
    keys.innerHTML =
      '<span><kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> move</span><span><kbd>Shift</kbd> run</span><span><kbd>E</kbd> get in / interact</span><span><kbd>Space</kbd> handbrake</span><span><kbd>R</kbd> radio</span><span><kbd>M</kbd> map</span>';

    this.setHeat(0, false, true);
  }

  // ---------------------------------------------------------------- minimap tap

  private readonly onMapDown = (e: PointerEvent): void => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    this.mapTap = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
    e.preventDefault(); // no focus move, no emulated mouse events after a touch
  };

  private readonly onMapUp = (e: PointerEvent): void => {
    const t = this.mapTap;
    this.mapTap = null;
    if (!t || t.id !== e.pointerId) return;
    if (performance.now() - t.t > MAP_TAP_MS || Math.hypot(e.clientX - t.x, e.clientY - t.y) > MAP_TAP_PX) return;
    e.preventDefault();
    this.onMapTap?.();
  };

  // ---------------------------------------------------------------- per-frame

  update(f: HudFrame): void {
    this.updateMoney(f);
    this.setHeat(f.heat, f.seen);
    this.updateHealth(f.health01);
    this.updateVehicle(f);
    setText(this.streetName, f.street);
    setText(this.streetDistrict, f.district);
    this.updatePrompt(f.prompt);

    if (this.sayTimer > 0) {
      this.sayTimer -= f.dt;
      if (this.sayTimer <= 0) this.sayBox.classList.add('is-hidden');
    }
    if (this.hintsTimer > 0) {
      this.hintsTimer -= f.dt;
      if (this.hintsTimer <= 0) this.hints.classList.add('fade');
    }
  }

  private updateMoney(f: HudFrame): void {
    if (f.money !== this.money.target) {
      const delta = f.money - this.money.target;
      const first = this.moneyShown < 0;
      this.money.setTarget(f.money, first);
      if (!first && Math.abs(delta) >= 5) this.popMoney(delta);
      if (!first) {
        this.moneyClass = delta > 0 ? 1 : -1;
        this.moneyClassTimer = 1.1;
      }
    }
    const shown = this.money.update(f.dt);
    if (shown !== this.moneyShown) {
      this.moneyShown = shown;
      setText(this.moneyAmt, formatMoney(shown));
    }
    if (this.moneyClassTimer > 0) {
      this.moneyClassTimer -= f.dt;
      if (this.moneyClassTimer <= 0) this.moneyClass = 0;
    }
    toggle(this.moneyBox, 'up', this.moneyClass > 0);
    toggle(this.moneyBox, 'down', this.moneyClass < 0);
  }

  private popMoney(delta: number): void {
    const pop = el('span', delta < 0 ? 'pop neg' : 'pop', `${delta < 0 ? '−' : '+'}£${formatMoney(Math.abs(delta))}`, this.moneyBox);
    this.later(() => pop.remove(), 1800);
  }

  private setHeat(level: number, seen: boolean, force = false): void {
    if (!force && level === this.heatLevel && seen === this.heatSeen) return;
    const levelChanged = level !== this.heatLevel;
    this.heatLevel = level;
    this.heatSeen = seen;
    const b = this.heatBox;
    b.className = `hud-heat bh-soot lvl${level}${level > 0 ? (seen ? ' seen' : ' searching') : ''}`;
    if (levelChanged) {
      this.heatBadge.innerHTML = HEAT_ICONS[Math.max(0, Math.min(4, level))]!;
      setText(this.heatName, heatName(level));
    }
    setText(this.heatState, heatState(level, seen));
    this.pips.forEach((p, i) => toggle(p, 'on', i < level));
  }

  /**
   * "Laid on the tarmac" (docs/POLICE.md §14): a red "UNIT DOWN" stamp slams onto the heat plate's left edge,
   * with a tally of crashes towards the next level off (`downs`/`needed`, needed 0 = no tally) or "−1" when a
   * level came off.
   */
  unitDown(downs: number, needed: number, dropped: boolean): void {
    this.heatBox.querySelector('.unit-down')?.remove();
    const s = el('div', `unit-down bh-stamp${dropped ? ' dropped' : ''}`, 'Unit down', this.heatBox);
    if (dropped) el('b', 'tally', '−1', s);
    else if (needed > 1) el('b', 'tally', `${downs}/${needed}`, s);
    this.later(() => s.classList.add('out'), 2600);
    this.later(() => s.remove(), 3100);
  }

  private updateHealth(h: number | null): void {
    const show = h !== null && h < 0.999;
    toggle(this.healthBox, 'idle', !show);
    if (show) this.healthFill.style.width = `${Math.round(h * 100)}%`;
  }

  private updateVehicle(f: HudFrame): void {
    const v = f.vehicle;
    toggle(this.vehBox, 'is-hidden', !v);
    if (!v) {
      this.radioText = null;
      this.radioTimer = 0;
      toggle(this.radioBox, 'show', false);
      return;
    }
    setText(this.vehName, v.name);
    if (v.mph !== this.vehMph) {
      this.vehMph = v.mph;
      setText(this.vehSpeed, String(Math.min(999, v.mph)).padStart(3, '0'));
      this.vehNeedle.style.transform = `rotate(${needleAngle(v.mph).toFixed(1)}deg)`;
    }
    setText(this.vehGear, v.gear < 0 ? 'R' : v.gear === 0 ? 'N' : String(v.gear));
    const hp = Math.round(Math.max(0, Math.min(1, v.health01)) * 100);
    if (hp !== this.vehHp) {
      this.vehHp = hp;
      this.vehHpArc.style.strokeDasharray = `${hp} 100`;
    }
    toggle(this.vehBox, 'warn', v.health01 <= 0.5 && v.health01 > 0.25);
    toggle(this.vehBox, 'crit', v.health01 <= 0.25);
    toggle(this.vehBox, 'burning', v.burning);
    toggle(this.vehWarn, 'on', v.health01 <= 0.5);
    toggle(this.vehAlert, 'on', v.burning);

    // Radio ticker: shown for a few seconds when you get in or the station changes.
    if (v.radio !== this.radioText) {
      const first = this.radioText === null;
      this.radioText = v.radio;
      setText(this.vehRadio, v.radio ? `♪ ${v.radio}` : touchWording('♪ radio off — press R', touchUi.enabled));
      toggle(this.vehRadio, 'on', !!v.radio);
      this.radioPointer.style.left = `${radioDialPos(v.radio).toFixed(1)}%`;
      // Getting in with the radio off says so once; a station change always shows.
      this.radioTimer = first && !v.radio ? 3 : v.radioNews ? RADIO_BULLETIN_S : RADIO_TICKER_S;
      toggle(this.radioBox, 'off', !v.radio);
      toggle(this.radioBox, 'news', !!v.radioNews && !!v.radio);
    }
    // The bottom band's order is subtitle > tip > radio: while the client is on the line the ticker
    // waits (its clock stops), and comes up whole once the line is over.
    const saying = this.sayTimer > 0;
    if (this.radioTimer > 0 && !saying) this.radioTimer -= f.dt;
    toggle(this.radioBox, 'show', this.radioTimer > 0 && !saying);
  }

  /** A subtitle line is on screen. */
  get saying(): boolean {
    return this.sayTimer > 0;
  }

  private updatePrompt(p: HudFrame['prompt']): void {
    if (!p) {
      if (!this.promptEl.hidden) this.promptEl.hidden = true;
      return;
    }
    if (this.promptEl.hidden) this.promptEl.hidden = false;
    setText(this.promptKey, p.key);
    setText(this.promptText, p.text);
    this.promptEl.style.left = `${p.sx.toFixed(1)}px`;
    this.promptEl.style.top = `${p.sy.toFixed(1)}px`;
  }

  // ---------------------------------------------------------------- pushes

  setObjective(o: ObjectiveInfo | null): void {
    toggle(this.objBox, 'is-empty', !o);
    if (!o) {
      this.objKey = '';
      this.objContentKey = '';
      this.objWarn.hidden = true;
      this.setObjExpanded(false, 0);
      return;
    }
    const compact = touchUi.enabled || !!this.objCompactMq?.matches;
    if (compact !== this.objBox.classList.contains('compact')) {
      toggle(this.objBox, 'compact', compact);
      if (compact) this.objBox.setAttribute('role', 'button');
      else this.objBox.removeAttribute('role');
      if (!compact) this.setObjExpanded(false, 0);
    }
    const key = JSON.stringify([o.title, o.text, o.hint, o.steps, o.tag, o.distance !== undefined, o.timer !== undefined, o.progress !== undefined]);
    if (key !== this.objKey) {
      this.objKey = key;
      // A new job, a new step or a new "what next": show the whole clipping for a moment, then the strip.
      const content = JSON.stringify([o.title, o.text, o.hint, o.steps, o.tag]);
      if (content !== this.objContentKey) {
        this.objContentKey = content;
        if (compact && !this.action) {
          this.objTapped = false;
          this.setObjExpanded(true, OBJ_OPEN_MS);
        }
      }
      setText(this.objTitle, o.title);
      setText(this.objTag, o.tag ?? 'THE JOB');
      toggle(this.objBox, 'next', !!o.tag);
      // The text is reworded for the touch controls like the hint always was ("Press E at the door" on a
      // phone read as a keyboard instruction next to a hint that said "tap the BOOK").
      setText(this.objText, touchWording(o.text, touchUi.enabled));
      setText(this.objHint, touchWording(o.hint ?? '', touchUi.enabled));
      this.objHint.hidden = !o.hint;
      this.objSteps.innerHTML = (o.steps ?? []).map((s) => `<li class="${s.done ? 'done' : ''}">${esc(s.text)}</li>`).join('');
      this.objSteps.hidden = !o.steps?.length;
      this.objDist.hidden = o.distance === undefined;
      this.objTimer.hidden = o.timer === undefined;
      this.objBar.hidden = o.progress === undefined;
      this.objMeta.hidden = o.distance === undefined && o.timer === undefined && o.progress === undefined;
    }
    if (o.distance !== undefined) setText(this.objDist, formatDistance(o.distance));
    if (o.timer !== undefined) {
      setText(this.objTimer, formatClock(o.timer));
      toggle(this.objTimer, 'low', o.timer < 10);
    }
    // The warning changes every frame (metres / countdown), so it's kept out of the layout key.
    this.objWarn.hidden = !o.warning;
    toggle(this.objBox, 'warned', !!o.warning);
    if (o.warning) {
      setText(this.objWarnText, o.warning.text);
      this.objWarnClock.hidden = o.warning.seconds === undefined;
      if (o.warning.seconds !== undefined) setText(this.objWarnClock, formatClock(o.warning.seconds));
    }
    if (o.progress !== undefined) this.objBarFill.style.width = `${Math.round(Math.max(0, Math.min(1, o.progress)) * 100)}%`;
  }

  /**
   * Action (src/ui/attention.ts): the compact clipping stays folded to its strip — it does not open by itself on
   * a new step, and an open one folds as the action starts. A tap still opens it. The extra lines go too: in a
   * chase only one toast is up at a time.
   */
  setAction(on: boolean): void {
    if (on === this.action) return;
    this.action = on;
    toggle(this.root, 'action', on);
    if (on && this.objBox.classList.contains('expanded') && !this.objTapped) this.setObjExpanded(false, 0);
    if (on) while (this.toasts.childElementCount > 1) (touchUi.enabled ? this.toasts.lastElementChild : this.toasts.firstElementChild)?.remove();
  }
  private action = false;
  /** The clipping is open because the player tapped it (not folded by the action rule). */
  private objTapped = false;

  /** Open (for `ms`, then fold back) or fold the compact objective clipping. */
  private setObjExpanded(open: boolean, ms: number): void {
    if (this.objFold !== null) {
      clearTimeout(this.objFold);
      this.timers.delete(this.objFold);
      this.objFold = null;
    }
    toggle(this.objBox, 'expanded', open);
    if (this.objBox.classList.contains('compact')) this.objBox.setAttribute('aria-expanded', String(open));
    else this.objBox.removeAttribute('aria-expanded');
    if (open && ms > 0) {
      const id = window.setTimeout(() => {
        this.timers.delete(id);
        this.objFold = null;
        this.setObjExpanded(false, 0);
      }, ms);
      this.timers.add(id);
      this.objFold = id;
    }
  }

  /** A tap on the strip opens it; a tap on the open clipping folds it. Fired on the press, like the knobs:
   *  a phone sends no click for a tap made while a thumb is on the stick. */
  private readonly onObjDown = (e: PointerEvent): void => {
    if (!this.objBox.classList.contains('compact') || this.objBox.classList.contains('is-empty')) return;
    e.preventDefault();
    e.stopPropagation();
    this.objTapped = !this.objBox.classList.contains('expanded');
    this.setObjExpanded(this.objTapped, OBJ_TAP_OPEN_MS);
  };

  /** `max`: how many may be up at once (one in action, src/ui/attention.ts); the newest stays. */
  toast(text: string, kind: ToastKind = 'info', seconds = 3.6, max?: number): void {
    const t = el('div', `toast bh-glass ${kind}`, text);
    // A phone stacks them under the objective clipping, where there is room for two at most: the newest
    // goes in first, straight under the clipping and always whole, and the column clips the oldest at
    // its foot (touch.css .hud-toasts).
    const touch = touchUi.enabled;
    if (touch) this.toasts.prepend(t);
    else this.toasts.appendChild(t);
    if (max === undefined) max = touch ? MAX_TOASTS_TOUCH : MAX_TOASTS;
    while (this.toasts.childElementCount > max) (touch ? this.toasts.lastElementChild : this.toasts.firstElementChild)?.remove();
    this.later(() => t.classList.add('out'), seconds * 1000);
    this.later(() => t.remove(), seconds * 1000 + 450);
  }

  say(speaker: string, text: string, seconds = 4): void {
    setText(this.sayWho, speaker);
    setText(this.sayLine, text);
    this.sayBox.classList.remove('is-hidden');
    this.sayTimer = seconds;
    // The tip slip (onboarding.css) steps up over the subtitle by exactly its height, however many lines
    // it wraps to between the thumbs.
    (this.root.closest('#ui-root') as HTMLElement | null ?? document.documentElement).style.setProperty('--hud-say-h', `${this.sayBox.offsetHeight}px`);
  }

  /**
   * The online pieces (src/online-ui): the connection in the chip (desktop) or the street plate (touch),
   * the bank line on the cash plate, the law strip, the cargo strip, the hotkey row. Only the DOM that
   * changed is touched; a field left out of `patch` keeps its value.
   */
  setOnline(patch: Partial<HudOnline>): void {
    const o = this.online;
    if ('net' in patch) {
      o.net = patch.net ?? null;
      const n = o.net;
      this.netChip.hidden = !n;
      if (n) {
        const bad = !n.good;
        toggle(this.netChip, 'down', bad);
        // A phone shows the short form (touch.css hides the city: the chip sits in the minimap's column,
        // left of the objective clipping and the toasts under it, so it has the minimap's width).
        setText(this.netChipText, bad ? `${n.city} · reconnecting…` : `${n.city} · ${n.population} on the streets · ${n.rtt} ms`);
        this.netChip.dataset.short = bad ? 'no line' : `${n.population} · ${n.rtt} ms`;
      }
    }
    if ('bank' in patch) {
      o.bank = patch.bank ?? null;
      this.bankLine.hidden = o.bank === null;
      if (o.bank !== null) setText(this.bankAmt, o.bank);
      toggle(this.moneyBox, 'has-bank', o.bank !== null);
      // The plates under the cash (heat, health, the bleeper) move down a line (ui.css / touch.css).
      toggle(this.root, 'has-bank', o.bank !== null);
    }
    if ('cargo' in patch) {
      o.cargo = patch.cargo ?? null;
      const c = o.cargo;
      this.cargoBox.hidden = !c;
      if (c) {
        setText(this.cargoText, c.text);
        toggle(this.cargoBox, 'hot', c.hot);
        this.cargoFill.style.width = `${Math.round(Math.max(0, Math.min(1, c.fill01)) * 100)}%`;
      }
    }
    if ('law' in patch) {
      o.law = patch.law ?? null;
      const l = o.law;
      this.lawBox.hidden = !l || l.lines.length === 0;
      if (l) {
        const html = l.lines.map((x) => `<div>${x}</div>`).join('');
        if (html !== this.lawHtml) {
          this.lawHtml = html;
          this.lawBox.innerHTML = html;
        }
        toggle(this.lawBox, 'hot', l.hot);
      }
      toggle(this.root, 'law-up', !this.lawBox.hidden);
    }
    if ('hotkeys' in patch) {
      o.hotkeys = patch.hotkeys ?? null;
      const k = o.hotkeys;
      this.hotkeyRow.hidden = !k || k.length === 0;
      if (k) {
        const html = k.map((h) => `<span><kbd>${esc(h.key)}</kbd> ${esc(h.label)}</span>`).join('');
        if (html !== this.hotkeyRow.innerHTML) {
          this.hotkeyRow.innerHTML = html;
          // A new set of keys (joining the city) gets its 14 s like the keyboard hints.
          this.hintsTimer = 14;
          this.hints.classList.remove('fade');
        }
      }
    }
  }

  /** The law strip (online) is up: the tip slip and the "what next" line leave the law to it. */
  get lawUp(): boolean {
    return !this.lawBox.hidden;
  }

  showDistrict(name: string, tags: string): void {
    setText(this.districtName, name);
    setText(this.districtTags, tags);
    this.districtCard.classList.remove('show');
    void this.districtCard.offsetWidth; // restart the animation
    this.districtCard.classList.add('show');
  }

  letterbox(on: boolean): void {
    toggle(this.bars, 'on', on);
    toggle(this.root, 'cinematic', on);
  }

  /** The bars are on: the paperwork (and the NOW ENTERING card) is stepped aside. */
  get cinematic(): boolean {
    return this.root.classList.contains('cinematic');
  }

  /** Pause menu up: step the HUD back so the menu doesn't sit on top of it (cash + heat stay). */
  setPaused(on: boolean): void {
    toggle(this.root, 'paused', on);
  }

  hitFlash(): void {
    this.flash.classList.remove('hit');
    void this.flash.offsetWidth;
    this.flash.classList.add('hit');
  }

  setVisible(v: boolean): void {
    toggle(this.root, 'is-hidden', !v);
  }

  /** Test/debug read-out. */
  state(): Record<string, unknown> {
    return {
      money: this.moneyShown,
      moneyTarget: this.money.target,
      heatLevel: this.heatLevel,
      heatSeen: this.heatSeen,
      objective: this.objBox.classList.contains('is-empty')
        ? null
        : {
            title: this.objTitle.textContent,
            text: this.objText.textContent,
            tag: this.objTag.textContent,
            compact: this.objBox.classList.contains('compact'),
            expanded: this.objBox.classList.contains('expanded'),
            distance: this.objDist.hidden ? null : this.objDist.textContent,
            warning: this.objWarn.hidden ? null : `${this.objWarnText.textContent}${this.objWarnClock.hidden ? '' : ` ${this.objWarnClock.textContent}`}`,
          },
      vehicleVisible: !this.vehBox.classList.contains('is-hidden'),
      speed: String(Math.max(0, this.vehMph)),
      radioTicker: this.radioBox.classList.contains('show') ? this.vehRadio.textContent : null,
      toasts: [...this.toasts.children].map((c) => c.textContent),
      prompt: this.promptEl.hidden ? null : this.promptText.textContent,
      street: this.streetName.textContent,
      district: this.streetDistrict.textContent,
      subtitle: this.sayBox.classList.contains('is-hidden') ? null : `${this.sayWho.textContent}: ${this.sayLine.textContent}`,
      online: {
        chip: this.netChip.hidden ? null : this.netChipText.textContent,
        chipShort: this.netChip.hidden ? null : this.netChip.dataset.short ?? null,
        bank: this.bankLine.hidden ? null : this.bankAmt.textContent,
        cargo: this.cargoBox.hidden ? null : this.cargoText.textContent,
        law: this.lawBox.hidden ? null : this.lawBox.textContent,
        hotkeys: this.hotkeyRow.hidden ? null : [...this.hotkeyRow.children].map((c) => c.textContent),
        hintsFaded: this.hints.classList.contains('fade'),
      },
    };
  }

  dispose(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.mapBox.removeEventListener('pointerdown', this.onMapDown);
    this.mapBox.removeEventListener('pointerup', this.onMapUp);
    this.objBox.removeEventListener('pointerdown', this.onObjDown);
    this.root.remove();
  }

  private later(fn: () => void, ms: number): void {
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      fn();
    }, ms);
    this.timers.add(id);
  }
}
